'use client'

import { useRef, useState } from 'react'

import { Control, FieldValues, Path } from 'react-hook-form'

import { NumberInput } from '~/components/component-library'

// The fields are half-width, so messages must fit on one line.
const INVALID_RANGE_MESSAGE = 'Invalid range'
const NEGATIVE_MESSAGE = 'Must be ≥ 0'

// Derives per-field errors for an optional min/max pair and drives them into
// RHF so the inputs show the red ring and message on blur. The fields are
// optional, so values are validated here rather than via the inputs' `min`
// prop (which can't carry a short message).
export const useRangeFieldErrors = ({
  min,
  max,
  setError,
  clearErrors,
}: {
  min: string
  max: string
  setError: (name: 'min' | 'max', error: { message: string }) => void
  clearErrors: (name: 'min' | 'max') => void
}) => {
  const isRangeInvalid = min !== '' && max !== '' && Number(min) > Number(max)

  const minError =
    min !== '' && Number(min) < 0
      ? NEGATIVE_MESSAGE
      : isRangeInvalid
        ? INVALID_RANGE_MESSAGE
        : undefined
  const maxError =
    max !== '' && Number(max) < 0
      ? NEGATIVE_MESSAGE
      : isRangeInvalid
        ? INVALID_RANGE_MESSAGE
        : undefined

  const [synced, setSynced] = useState<{ min?: string; max?: string }>({})
  if (synced.min !== minError || synced.max !== maxError) {
    setSynced({ min: minError, max: maxError })
    if (minError) setError('min', { message: minError })
    else clearErrors('min')
    if (maxError) setError('max', { message: maxError })
    else clearErrors('max')
  }

  return { minError, maxError }
}

// Paired minimum/maximum inputs for a filter popover: Enter advances from min
// to max, then hands focus to the popover's primary action.
export const RangeInputs = <FormType extends FieldValues>({
  control,
  minName,
  maxName,
  onComplete,
  placeholder = '0',
  prefix,
  decimalScale,
}: {
  control: Control<FormType>
  minName: Path<FormType>
  maxName: Path<FormType>
  onComplete: () => void
  placeholder?: string
  prefix?: string
  decimalScale?: number
}) => {
  const maxRef = useRef<HTMLInputElement>(null)

  return (
    <div className="grid grid-cols-2 gap-4">
      <NumberInput
        control={control}
        inputName={minName}
        label="Minimum"
        size="small"
        placeholder={placeholder}
        prefix={prefix}
        decimalScale={decimalScale}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            maxRef.current?.focus()
          }
        }}
      />
      <NumberInput
        control={control}
        inputName={maxName}
        label="Maximum"
        size="small"
        placeholder={placeholder}
        prefix={prefix}
        decimalScale={decimalScale}
        ref={maxRef}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onComplete()
          }
        }}
      />
    </div>
  )
}
